import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { withConfluenceStyleWrapper } from '../confluenceStyleWrapper';
import { fg } from '@atlaskit/platform-feature-flags';

const PlatformDonutChart = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_donutchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.DonutChart })),
);

const Title = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_donutchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.DonutChart.elements.Title })),
);

const SubTitle = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_donutchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.DonutChart.elements.SubTitle })),
);

const MarkLabels = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_donutchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.DonutChart.elements.MarkLabels })),
);

const Layout = React.lazy(() =>
		// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_donutchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.DonutChart.elements.Layout })),
);

const Theme = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_donutchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.DonutChart.elements.Theme })),
);

type PlatformDonutChartProps = React.ComponentProps<typeof PlatformDonutChart>;

/**
 * Added props:
 * - showMarkLabels - show/hide the labels on top of each slice of the Donut, maps to the MarkLabels primitive
 * - colorPalette - allow the user to match color to attribute
 *
 * Excluded props:
 * - children - we will not be supporting primitives/elements to compose the chart
 *
 * Limited props:
 * - labelAccessor - only allow non function types
 * - valueAccessor - only allow non function types
 * - colorAccessor - only allow non function types
 */
export type DonutChartProps = Pick<PlatformDonutChartProps, 'width' | 'height' | 'data'> & {
	colorAccessor: number | string;
	colorPalette?: string[] | { key: string; value: string }[];
	innerRadius?: number;
	labelAccessor: number | string;
	outerRadius?: number;
	showBorder?: boolean;
	showMarkLabels?: boolean;
	subtitle?: string;
	title?: string;
	valueAccessor: number | string;
};

const BaseDonutChart = (props: Parameters<RenderFn>[0]) => {
	const {
		width,
		height,
		data,
		colorAccessor,
		labelAccessor,
		valueAccessor,
		title,
		subtitle,
		showMarkLabels,
		innerRadius,
		outerRadius,
		showBorder,
		colorPalette,
	} = props.forgeDoc.props as DonutChartProps;

	return (
		<PlatformDonutChart
			width={width}
			height={height ? height : 400}
			data={data}
			colorAccessor={colorAccessor}
			valueAccessor={valueAccessor}
			labelAccessor={labelAccessor}
			innerRadius={innerRadius}
			outerRadius={outerRadius}
		>
			<MarkLabels hide={!showMarkLabels} />
			{title && <Title>{title}</Title>}
			{subtitle && <SubTitle>{subtitle}</SubTitle>}
			{fg('enable-margin-property-for-ui-kit-chart-components') && (
				<Layout showBorder={showBorder} />
			)}
			{colorPalette && fg('platform-enable-customisation-colors-for-charts') && (
				<Theme fillStyles={colorPalette} />
			)}
		</PlatformDonutChart>
	);
};

export const DonutChart: (props: Parameters<RenderFn>[0]) => JSX.Element =
	withConfluenceStyleWrapper(BaseDonutChart);
